import { useState } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { useOrg } from '../../../contexts/OrgContext'
import { usePolicy } from '../../../contexts/PolicyContext'

function BrandingApplicationModal({ orgBrandConfig, orgName, templates, onClose, onComplete }) {
  const { user } = useAuth()
  const { currentOrg } = useOrg() || {}
  const { updateTemplate } = usePolicy()
  const [confirmed, setConfirmed] = useState(false)
  const [applying, setApplying] = useState(false)
  const [progress, setProgress] = useState({ current: 0, total: 0 })

  const firmName = orgBrandConfig?.firm_display_name || orgName || 'Your Organization'
  const disclaimer = orgBrandConfig?.standard_disclaimer || ''
  const eligibleTemplates = templates.filter(t => t.structured_content)

  // Trademark patterns to preserve
  const trademarkPatterns = [
    /©\s*\d{4}\s*Sidewalk Security Advisors/g,
    /Prepared by Sidewalk Security Advisors/g
  ]

  const applyBranding = async () => {
    setApplying(true)
    setProgress({ current: 0, total: eligibleTemplates.length })

    let updated = 0
    for (const template of eligibleTemplates) {
      setProgress(prev => ({ ...prev, current: prev.current + 1 }))

      try {
        // Preserve trademark strings
        const preserved = []
        let json = JSON.stringify(template.structured_content)
        trademarkPatterns.forEach(pattern => {
          const matches = json.match(pattern)
          if (matches) matches.forEach(m => { if (!preserved.includes(m)) preserved.push(m) })
        })

        // Replace firm name (but not in trademark patterns)
        // First, replace trademarks with placeholders
        preserved.forEach((tm, i) => {
          json = json.split(tm).join(`__PRESERVE_${i}__`)
        })

        // Run the replacement
        json = json.split('Sidewalk Security Advisors').join(firmName)

        // Restore trademarks
        preserved.forEach((tm, i) => {
          json = json.split(`__PRESERVE_${i}__`).join(tm)
        })

        const updatedSc = JSON.parse(json)

        // Apply to markdown content too
        let updatedContent = template.content || ''
        if (updatedContent) {
          preserved.forEach((tm, i) => {
            updatedContent = updatedContent.split(tm).join(`__PRESERVE_${i}__`)
          })
          updatedContent = updatedContent.split('Sidewalk Security Advisors').join(firmName)
          preserved.forEach((tm, i) => {
            updatedContent = updatedContent.split(`__PRESERVE_${i}__`).join(tm)
          })
        }

        // Append disclaimer to related_documents if set
        if (disclaimer && updatedSc.related_documents) {
          const hasDisclaimer = updatedSc.related_documents.some(d =>
            typeof d === 'string' && d.includes('Disclaimer')
          )
          if (!hasDisclaimer) {
            updatedSc.related_documents.push(`Standard Disclaimer: ${disclaimer}`)
          }
        }

        await updateTemplate(template.id, {
          structured_content: updatedSc,
          content: updatedContent,
          org_edited: true
        })

        // Write revision record
        if (currentOrg?.id) {
          await supabase.from('org_template_revisions').insert({
            template_id: template.id,
            org_id: currentOrg.id,
            content: updatedContent || JSON.stringify(updatedSc),
            edit_method: 'brand_apply',
            change_summary: 'Brand applied: firm name and disclaimer updated',
            edited_by: user?.id
          })
        }

        updated++
      } catch (err) {
        console.error(`Failed to apply branding to ${template.title}:`, err)
      }
    }

    setApplying(false)
    onComplete(updated)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-lg">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Apply Your Branding to All Templates</h3>

        {applying ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Updating {progress.current} of {progress.total} templates...
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  "<span className="font-medium">{firmName}</span>" will replace "Sidewalk Security Advisors" in policy content
                </p>
              </div>
              {disclaimer && (
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm text-gray-700 dark:text-gray-300">Your standard disclaimer will be added to related documents</p>
                </div>
              )}
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">The SSA trademark line in document footers will be preserved</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              This will update <span className="font-semibold">{eligibleTemplates.length}</span> template{eligibleTemplates.length !== 1 ? 's' : ''} in your library.
            </p>

            <label className="flex items-center gap-2 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">I understand this will modify all org library templates</span>
            </label>

            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="btn-secondary">Cancel</button>
              <button
                onClick={applyBranding}
                disabled={!confirmed || eligibleTemplates.length === 0}
                className="btn-primary disabled:opacity-50"
              >
                Apply to All
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default BrandingApplicationModal
